import {
  ExcursionCalendarDay,
  GetExcursionCalendarRequest,
  GetExcursionCalendarResponse,
  SearchOnlyExcursionPackagesRequest
} from '@appTypes/api.types';
import { DynamicDateClassItem } from '@shared/components/dates/dynamic-date-picker.abstract';

/** the CSS class of a calendar day with excursions on sale (styles.scss, next to the tours calendar's flight-* classes) */
export const EXCURSION_ON_SALE_CLASS = 'excursion-on-sale';

/** How many nearest on-sale days the empty-result message offers. */
export const NEAREST_DAYS_SHOWN = 3;

/** What one search asked, as the calendar of that month is keyed: the same destination, filters and party - never the day. */
export const excursionCalendarParams = (
  request: Pick<
    SearchOnlyExcursionPackagesRequest,
    'countryCode' | 'cityUIDs' | 'excursionTypeUIDs' | 'adults' | 'children'
  >,
  year: number,
  month: number
): GetExcursionCalendarRequest => ({
  countryCode: request.countryCode ?? null,
  cityUIDs: request.cityUIDs?.length ? [...request.cityUIDs].sort((a, b) => a.localeCompare(b)) : null,
  excursionTypeUIDs: request.excursionTypeUIDs?.length
    ? [...request.excursionTypeUIDs].sort((a, b) => a.localeCompare(b))
    : null,
  adults: request.adults ?? 1,
  children: request.children ?? 0,
  year,
  month
});

/** The month of a search's day ('yyyy-MM-dd'); null when the day is not a date. */
export const excursionCalendarParamsOfSearch = (
  request: SearchOnlyExcursionPackagesRequest
): GetExcursionCalendarRequest | null => {
  const m = /^(\d{4})-(\d{2})/.exec(request?.date ?? '');
  if (!m) return null;
  return excursionCalendarParams(request, Number(m[1]), Number(m[2]));
};

/** The store key of one calendar month: the parameters, canonically ordered. */
export const excursionCalendarKey = (params: GetExcursionCalendarRequest): string =>
  JSON.stringify([
    params.countryCode ?? null,
    params.cityUIDs ?? null,
    params.excursionTypeUIDs ?? null,
    params.adults ?? 1,
    params.children ?? 0,
    params.year,
    params.month
  ]);

const dayNumber = (isoDate: string): number => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
  return m ? Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000 : Number.NaN;
};

/** The on-sale days of a month closest to a day (before or after), in date order - the chips of the empty-result message. */
export const nearestOnSaleDays = (
  days: ExcursionCalendarDay[] | null | undefined,
  isoDate: string,
  count = NEAREST_DAYS_SHOWN
): ExcursionCalendarDay[] => {
  const target = dayNumber(isoDate);
  if (Number.isNaN(target)) return [];
  return (days ?? [])
    .filter((d) => (d.excursions ?? 0) > 0 && d.date?.slice(0, 10) !== isoDate.slice(0, 10))
    .map((d) => ({ day: d, distance: Math.abs(dayNumber(d.date) - target) }))
    .filter((x) => !Number.isNaN(x.distance))
    .sort((a, b) => a.distance - b.distance || a.day.date.localeCompare(b.day.date))
    .slice(0, count)
    .map((x) => x.day)
    .sort((a, b) => a.date.localeCompare(b.date));
};

/** True when the calendar says excursions are on sale on the day; null when the calendar does not cover it. */
export const isDayOnSale = (days: ExcursionCalendarDay[] | null | undefined, isoDate: string): boolean | null => {
  const day = (days ?? []).find((d) => d.date?.slice(0, 10) === isoDate?.slice(0, 10));
  return day ? (day.excursions ?? 0) > 0 : null;
};

/** The day marks of a month: a green day with the number of excursions under it (mockup option B); a day with none stays plain. */
export const excursionCalendarDateClassItems = (
  response: GetExcursionCalendarResponse | null | undefined
): DynamicDateClassItem[] =>
  (response?.days ?? [])
    .filter((d) => (d.excursions ?? 0) > 0)
    .map((d) => {
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d.date);
      return {
        date: m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(d.date),
        cssClass: [EXCURSION_ON_SALE_CLASS],
        badge: String(d.excursions)
      };
    });

/** 'yyyy-MM-dd' -> a local Date (no time-zone shift), null when it is not a date. */
export const localDateOf = (isoDate: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate ?? '');
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};

/** What the empty-result message knows about the searched day, once the calendar of its month is in. */
export interface ExcursionsEmptyDayInfo {
  /** the searched day, 'yyyy-MM-dd' */
  date: string;
  /** false = the calendar says nothing operates that day; true = it does (the search still found nothing) */
  dayOnSale: boolean;
  nearestDays: ExcursionCalendarDay[];
}
